// シンプルなタイルマップ：2D配列を Phaser.Tilemap に変換し、衝突判定を構築する
// 16x16のKenneyタイルを2倍にスケールして32x32相当で描画する想定
import Phaser from 'phaser';

export const TILE_SIZE = 16; // 元素材
export const TILE_SCALE = 2; // 表示倍率
export const DISPLAY_TILE = TILE_SIZE * TILE_SCALE; // = 32

// マップ1枚分のデータ：単一タイルセット前提
export interface MapData {
  tilesetKey: string; // BootScene でロードしたスプライトシートのキー
  // 各セルはタイルインデックス（0以上）。-1=空セル。
  layers: {
    ground: number[][]; // 床（衝突なし）
    decoration?: number[][]; // 飾り（衝突なし、半透明はしない）
    walls?: number[][]; // 壁（衝突あり）
  };
}

// MapData から Phaser のタイルマップを構築する
export function buildTilemap(scene: Phaser.Scene, data: MapData) {
  const rows = data.layers.ground.length;
  const cols = data.layers.ground[0]?.length ?? 0;

  // make.tilemap で空の枠だけ用意し、各レイヤーは個別に putTileAt する
  const map = scene.make.tilemap({
    tileWidth: TILE_SIZE,
    tileHeight: TILE_SIZE,
    width: cols,
    height: rows,
  });
  // 16x16のスプライトシートとして登録したテクスチャをタイルセットに採用
  const tileset = map.addTilesetImage(data.tilesetKey, data.tilesetKey, TILE_SIZE, TILE_SIZE, 0, 0);
  if (!tileset) throw new Error(`Tileset add failed: ${data.tilesetKey}`);

  // 床レイヤー
  const groundLayer = map.createBlankLayer('ground', tileset, 0, 0)!;
  applyData(groundLayer, data.layers.ground);
  groundLayer.setScale(TILE_SCALE);

  // 飾りレイヤー（壁の上に描かれる：床→飾り→壁の順）
  let decoLayer: Phaser.Tilemaps.TilemapLayer | undefined;
  if (data.layers.decoration) {
    decoLayer = map.createBlankLayer('deco', tileset, 0, 0)!;
    applyData(decoLayer, data.layers.decoration);
    decoLayer.setScale(TILE_SCALE);
  }

  // 壁レイヤー
  let wallsLayer: Phaser.Tilemaps.TilemapLayer | undefined;
  if (data.layers.walls) {
    wallsLayer = map.createBlankLayer('walls', tileset, 0, 0)!;
    applyData(wallsLayer, data.layers.walls);
    wallsLayer.setScale(TILE_SCALE);
    // 壁レイヤー全体に衝突判定（タイルが存在するセルだけ自動でブロック扱い）
    wallsLayer.setCollisionByExclusion([-1]);
  }

  return {
    map,
    groundLayer,
    decoLayer,
    wallsLayer,
    widthPx: cols * DISPLAY_TILE,
    heightPx: rows * DISPLAY_TILE,
  };
}

function applyData(layer: Phaser.Tilemaps.TilemapLayer, data: number[][]): void {
  for (let y = 0; y < data.length; y++) {
    const row = data[y]!;
    for (let x = 0; x < row.length; x++) {
      const idx = row[x]!;
      if (idx >= 0) layer.putTileAt(idx, x, y);
    }
  }
}
